import React, { useState } from "react";
import axios from "axios";
import { MessageCircle, Calendar, Clock, Users } from "lucide-react";
import { toast } from "sonner";
import { buildWhatsAppReservationLink } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMES = ["11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00"];

const initialState = {
  name: "",
  email: "",
  phone: "",
  date: "",
  time: "18:00",
  guests: 2,
  message: "",
};

const Reservation = () => {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submitToBackend = async () => {
    await axios.post(`${API}/reservations`, {
      ...form,
      guests: Number(form.guests),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.date || !form.time) {
      toast.error("Veuillez remplir tous les champs requis.");
      return;
    }
    setLoading(true);
    try {
      await submitToBackend();
      toast.success("Réservation enregistrée — nous revenons vers vous très vite !");
      const link = buildWhatsAppReservationLink(form);
      window.open(link, "_blank", "noopener,noreferrer");
      setForm(initialState);
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Réessayez ou contactez-nous via WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div data-testid="reservation-page" className="bg-[#F9F6F0]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-0">
          <img
            src="https://images.pexels.com/photos/8775400/pexels-photo-8775400.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400"
            alt="Dégustation"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F9F6F0] via-[#F9F6F0]/80 to-[#F9F6F0]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold">Degustazione</p>
            <h1 className="font-heading text-5xl md:text-6xl text-[#1A1617] mt-3 leading-tight">
              Réservez votre <em className="italic text-[#5B2333]">dégustation</em>
            </h1>
            <p className="text-base text-[#5C5651] mt-6 leading-relaxed max-w-xl">
              Venez découvrir notre cave à Sint-Pieters-Leeuw. Une dégustation
              guidée de nos cuvées des Pouilles, entre passionnés. Remplissez le
              formulaire, nous confirmons votre créneau dans la journée.
            </p>

            <div className="mt-12 space-y-5">
              {[
                { icon: Calendar, label: "Sur rendez-vous", value: "7j/7 selon disponibilité" },
                { icon: Users, label: "Petits groupes", value: "De 2 à 10 personnes" },
                { icon: Clock, label: "Durée", value: "Environ 1h30 — 5 cuvées" },
              ].map((row) => (
                <div key={row.label} className="flex items-start gap-4">
                  <row.icon className="w-5 h-5 text-[#5B2333] mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold">{row.label}</p>
                    <p className="text-sm text-[#1A1617] mt-1">{row.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="reservation-form"
            className="bg-white border border-[#E8E2D2] p-8 md:p-10 space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Nom complet" required>
                <input
                  value={form.name}
                  onChange={update("name")}
                  required
                  data-testid="res-name"
                  className="w-full bg-transparent border-b border-[#E8E2D2] focus:border-[#5B2333] outline-none py-2 text-[#1A1617]"
                />
              </Field>
              <Field label="Téléphone" required>
                <input
                  value={form.phone}
                  onChange={update("phone")}
                  required
                  data-testid="res-phone"
                  className="w-full bg-transparent border-b border-[#E8E2D2] focus:border-[#5B2333] outline-none py-2 text-[#1A1617]"
                />
              </Field>
            </div>

            <Field label="Email" required>
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                data-testid="res-email"
                className="w-full bg-transparent border-b border-[#E8E2D2] focus:border-[#5B2333] outline-none py-2 text-[#1A1617]"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <Field label="Date" required>
                <input
                  type="date"
                  value={form.date}
                  onChange={update("date")}
                  min={today}
                  required
                  data-testid="res-date"
                  className="w-full bg-transparent border-b border-[#E8E2D2] focus:border-[#5B2333] outline-none py-2 text-[#1A1617]"
                />
              </Field>
              <Field label="Heure" required>
                <select
                  value={form.time}
                  onChange={update("time")}
                  required
                  data-testid="res-time"
                  className="w-full bg-transparent border-b border-[#E8E2D2] focus:border-[#5B2333] outline-none py-2 text-[#1A1617]"
                >
                  {TIMES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>
              <Field label="Personnes" required>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={form.guests}
                  onChange={update("guests")}
                  required
                  data-testid="res-guests"
                  className="w-full bg-transparent border-b border-[#E8E2D2] focus:border-[#5B2333] outline-none py-2 text-[#1A1617]"
                />
              </Field>
            </div>

            <Field label="Message (optionnel)">
              <textarea
                value={form.message}
                onChange={update("message")}
                rows={3}
                data-testid="res-message"
                className="w-full bg-transparent border-b border-[#E8E2D2] focus:border-[#5B2333] outline-none py-2 text-[#1A1617] resize-none"
                placeholder="Préférences, allergies, occasion spéciale…"
              />
            </Field>

            <button
              type="submit"
              disabled={loading}
              data-testid="res-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#5B2333] text-white px-6 py-4 text-sm tracking-[0.18em] uppercase font-semibold hover:bg-[#4A1525] transition-colors disabled:opacity-60"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
              {loading ? "Envoi…" : "Envoyer ma demande"}
            </button>
            <p className="text-[11px] text-[#5C5651] text-center italic">
              Votre demande est enregistrée et un message WhatsApp pré-rempli s&apos;ouvrira pour confirmer.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.2em] uppercase text-[#C5A059] font-semibold">
      {label}{required && <span className="text-[#5B2333]"> *</span>}
    </span>
    {children}
  </label>
);

export default Reservation;
